/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

import { getGlobalParams, getCustomIntl } from 'config/config'
import { updateBreadCrumb, updateHtmlTitle, handleComparisonItems } from 'utils/interaction'
import { syncFilterData, mergeSplittingWords } from 'utils/filter'
import { dispatchFundPage, dispatchFundListExpand, dispatchFundListGroupBy, dispatchFundListOrder } from 'utils/tracking'
import { updateUrlWithParams, getLatestUrlParams, updateCanonicalUrl, removeCanonicalUrl } from 'utils/url'

import FundsList from '../../../templates/FundsList'
import HomepageLevelMessage from '../../../atoms/HomepageLevelMessage'

import filterRules from './filterRules'
import getGroupRules from './fundsGroupConfig'
import columns from './columns'

import { fundItemsSubject, nextByFilter } from '../../../../data/subjects/fundItems'
import { nextByGetFundsCountInfo, nextByAddFav } from '../../../../data/subjects/fundInfoItems'
import { createFilterStore, getFilterStore, clearFilterStore } from '../../../../data/subjects/filterStoreProducer'
import { getFundList, getSplittingWords } from '../services'

import './style.scss'

const TWFundListView = () => {
  const { terms, fundComparisonCart, funds2CartJob, cart2FundJob, hasFundComparison, countryConfig } = getGlobalParams()
  const { urlParamsMapping } = countryConfig
  const queryParams = getLatestUrlParams({ urlParamsMapping })
  const { defaultGroupOption, groupOptions } = getGroupRules()

  const [tableData, setTableData] = useState()
  const tabeDataRef = useRef(tableData)

  const setTabelDataRef = (x) => {
    tabeDataRef.current = x
    setTableData(x)
  }

  const intl = getCustomIntl()
  const [originFunds, setOriginFunds] = useState()
  const originFundsRef = useRef(originFunds)

  const setOriginFundsRef = (x) => {
    originFundsRef.current = x
    setOriginFunds(x)
  }

  const [filteredFunds, setFilteredFunds] = useState()
  const filteredFundsRef = useRef(filteredFunds)

  const setFilteredFundsRef = (x) => {
    filteredFundsRef.current = x
    setFilteredFunds(x)
  }

  const [filterData, setFilterData] = useState()
  const [fundsCountInfo, setFundsCountInfo] = useState()
  const [fundsError, setFundsError] = useState()
  const [exportLinkUrl, setExportLinkUrl] = useState('')

  const [selectedGroupOption, selectGroupOption] = useState()
  const [addedEventListener, setAddedEventListener] = useState(false)

  const selectedGroupOptionRef = useRef(selectedGroupOption)

  const selectGroupOptionRef = (x) => {
    selectedGroupOptionRef.current = x
    selectGroupOption(x)
  }

  const footerInfo = {
    id: `fil-fund-list-footer`,
    contents: [intl.formatMessage({ id: 'disclaimers.riskRating' })]
  }

  const getGroupOption = (optionValue) => {
    const { key, order, value } = _.find(groupOptions, (option) => option.value === optionValue || option.key === optionValue)
    updateUrlWithParams({
      query: {
        grouping: key
      }
    })

    return key ? { groupByOptions: value, customGroupOrder: order } : {}
  }

  const setDownloadUrl = (avaliableFunds) => {
    const shareClassIds = _(avaliableFunds)
      .map((item) => item.shareClassId)
      .join(',')

    const fullDownloadUrl = `/xapi/fundList/excel${shareClassIds ? `?scIds=${shareClassIds}` : ''}`

    setExportLinkUrl(fullDownloadUrl)
  }

  const handleGroupChange = (e) => {
    selectGroupOptionRef(getGroupOption(e.value))
    const selectedGroupBy = groupOptions.find((option) => option.value === e.value)
    dispatchFundListGroupBy(selectedGroupBy.label)
  }

  const selectFilterConf = {
    id: 'tw-table-header-fund-group-selector',
    value: queryParams.grouping || defaultGroupOption,
    options: JSON.stringify(groupOptions)
  }

  const getFundsCountInfo = (filteredTableData) => {
    setFundsCountInfo({
      fundsNumerator: filteredTableData.length,
      totalFundsCount: originFundsRef.current.length
    })

    return {
      fundsNumerator: filteredTableData.length,
      totalFundsCount: originFundsRef.current.length
    }
  }

  const updateComparisonStatus = (shareClassInfo) => {
    handleComparisonItems(shareClassInfo)

    setTabelDataRef({
      ...tabeDataRef.current,
      columns: columns({ handleComparisonItems: updateComparisonStatus })
    })
  }

  const handleTableCallback = ({ sortOrder, key, fromHandleSort }) => {
    updateUrlWithParams({
      query: {
        sortItem: key,
        sortOrder
      }
    })

    if (fromHandleSort) {
      let targetItem
      columns({ handleComparisonItems: () => { } }).forEach((item) => {
        if (item.key) {
          if (item.key === key) {
            targetItem = item
          }
        } else {
          const orderItem = item.children.find((childItem) => childItem.key === key)
          if (orderItem) {
            targetItem = orderItem
          }
        }
      })
      const content = sortOrder === 'desc' ? 'Descending' : 'Ascending'
      dispatchFundListOrder(`${targetItem.title} Order`, content)
    }
  }

  const handleExpandAllCallback = (expandedAllStatus) => {
    dispatchFundListExpand(expandedAllStatus ? 'Expand': 'Collapse')
  }

  const subscribeFilteredFunds = () => {
    const fundItemObservable = fundItemsSubject.subscribe({
      next: (filteredFundsList) => {
        const currentQueryParams = getLatestUrlParams()
        setDownloadUrl(filteredFundsList)
        setFilteredFundsRef(filteredFundsList)
        setTabelDataRef({
          columns: columns({
            handleComparisonItems: updateComparisonStatus,
            sortInfo: { sortItem: currentQueryParams?.sortItem, sortOrder: currentQueryParams?.sortOrder }
          }),
          dataSource: filteredFundsList
        })

        nextByGetFundsCountInfo(getFundsCountInfo(filteredFundsList))
      }
    })

    return fundItemObservable
  }

  useEffect(async () => {
    const currentQueryParams = getLatestUrlParams()

    const groupingParam = currentQueryParams.grouping
    const groupingOptions = groupingParam || (defaultGroupOption === 'none' ? '' : defaultGroupOption)

    const initGroupOption = getGroupOption(groupingOptions)

    selectGroupOptionRef(initGroupOption)
    setTabelDataRef({
      columns: columns({ handleComparisonItems: updateComparisonStatus })
    })
    setOriginFundsRef([])
    // Call Django function to update breadcrumb
    updateBreadCrumb()
    updateHtmlTitle(intl.formatMessage({ id: 'fundList.fundListTitle' }))
    dispatchFundPage()
    updateCanonicalUrl(countryConfig.canonicalUrlMaps)

    const fullFundListPromise = getFundList()
    const splittingWordsPromise = getSplittingWords()

    const [fullFundList, splittingWords] = await Promise.all([fullFundListPromise, splittingWordsPromise])

    if (!fullFundList) {
      setFundsError(true)
    } else {
      setOriginFundsRef(fullFundList)

      // init filter data
      const fixedRule = { ...filterRules, filter: mergeSplittingWords(filterRules, splittingWords) }
      const siftedFilterData = createFilterStore('FUND_LIST', { fundItems: fullFundList, rule: fixedRule, terms })
        .updateKeyOrderFromUrl(window.location.href)
        .calcFilterData()
      const finalFilterData = syncFilterData(siftedFilterData)
      setFilterData(finalFilterData)
      nextByFilter(fullFundList, finalFilterData)

      if (hasFundComparison) {
        const currentItems = sessionStorage.getItem(fundComparisonCart) ? JSON.parse(sessionStorage.getItem(fundComparisonCart)) : []
        funds2CartJob.funds2CartTrigger(currentItems)
        cart2FundJob.cart2FundSubject.subscribe({
          next: () => {
            setTabelDataRef({
              ...tabeDataRef.current,
              columns: columns({
                handleComparisonItems: updateComparisonStatus
              })
            })
          }
        })
      }
    }
  }, [])

  useEffect(() => {
    // Will cause memory leak
    const fundItemObservable = subscribeFilteredFunds()

    return () => {
      funds2CartJob?.funds2CartTrigger([])
      removeCanonicalUrl()
      fundItemObservable.unsubscribe()
      clearFilterStore('FUND_LIST')
    }
  }, [])

  const updateFilterData = (newFilters) => {
    setExportLinkUrl('')
    setTimeout(() => {
      const filterStore = getFilterStore('FUND_LIST')
      nextByFilter(filterStore.getFundItems(), newFilters)
    }, 0)
  }

  return (
    <React.Fragment>
      <div className="tw-funds-list-page">
        {fundsError ? (
          <HomepageLevelMessage type="fundList" />
        ) : (
          <FundsList
            {...{
              id: 'tw-funds-list',
              tableData: tabeDataRef.current,
              filterData,
              updateFilterData,
              totalResultConfig: {
                displayNumerator: true,
                displayDownloadLink: true,
                fundsCountInfo
              },
              selectedGroupOption: selectedGroupOptionRef.current,
              groupFilterConfig: {
                selectFilterConf,
                handleGroupChange
              },
              tableCallback: handleTableCallback,
              expandAllCallback: handleExpandAllCallback,
              footerInfo,
              downloadOptions: {
                exportLinkContent: intl.formatMessage({ id: 'fundList.table.exportResult' }),
                url: exportLinkUrl
              }
            }}
          />
        )}
      </div>
    </React.Fragment>
  )
}

export default TWFundListView
